/**
 * Style Tab - Colors and fonts
 */

import type { SkinStyleConfig } from '../../../public/deals/components/slot-machine/themes/skin-types';

interface StyleTabProps {
  config: SkinStyleConfig;
  onUpdate: (config: SkinStyleConfig) => void;
}

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 p-1 border border-gray-300 rounded cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
        />
      </div>
    </div>
  );
}

export default function StyleTab({ config, onUpdate }: StyleTabProps) {
  const updateField = (field: keyof SkinStyleConfig, value: string) => {
    onUpdate({ ...config, [field]: value });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold mb-4">Style Configuration</h2>
        <p className="text-sm text-gray-500 mb-6">
          Customize the colors and fonts for your slot machine skin.
        </p>
      </div>

      {/* Main Colors */}
      <div>
        <h3 className="text-md font-medium mb-4">Main Colors</h3>
        <div className="grid grid-cols-2 gap-4">
          <ColorField
            label="Primary Color"
            value={config.primary}
            onChange={(v) => updateField('primary', v)}
          />
          <ColorField
            label="Secondary Color"
            value={config.secondary}
            onChange={(v) => updateField('secondary', v)}
          />
          <ColorField
            label="Accent Color"
            value={config.accent}
            onChange={(v) => updateField('accent', v)}
          />
          <ColorField
            label="Win Glow Color"
            value={config.winGlow}
            onChange={(v) => updateField('winGlow', v)}
          />
        </div>
      </div>

      {/* Background Colors */}
      <div>
        <h3 className="text-md font-medium mb-4">Background Colors</h3>
        <div className="grid grid-cols-2 gap-4">
          <ColorField
            label="Main Background"
            value={config.background}
            onChange={(v) => updateField('background', v)}
          />
          <ColorField
            label="Reel Background"
            value={config.reelBackground}
            onChange={(v) => updateField('reelBackground', v)}
          />
        </div>
      </div>

      {/* Text Colors */}
      <div>
        <h3 className="text-md font-medium mb-4">Text Colors</h3>
        <div className="grid grid-cols-2 gap-4">
          <ColorField
            label="Primary Text"
            value={config.textPrimary}
            onChange={(v) => updateField('textPrimary', v)}
          />
          <ColorField
            label="Secondary Text"
            value={config.textSecondary}
            onChange={(v) => updateField('textSecondary', v)}
          />
          <ColorField
            label="Win Text"
            value={config.winText}
            onChange={(v) => updateField('winText', v)}
          />
        </div>
      </div>

      {/* Fonts */}
      <div>
        <h3 className="text-md font-medium mb-4">Typography</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Main Font
            </label>
            <input
              type="text"
              value={config.fontFamily || ''}
              onChange={(e) => updateField('fontFamily', e.target.value)}
              placeholder="Inter, sans-serif"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numbers Font
            </label>
            <input
              type="text"
              value={config.fontFamilyNumbers || ''}
              onChange={(e) => updateField('fontFamilyNumbers', e.target.value)}
              placeholder="JetBrains Mono, monospace"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      <div>
        <h3 className="text-md font-medium mb-4">Preview</h3>
        <div
          className="rounded-lg p-6 border"
          style={{ backgroundColor: config.background }}
        >
          <div
            className="rounded-lg p-4 mb-4"
            style={{ backgroundColor: config.reelBackground }}
          >
            <p style={{ color: config.textPrimary, fontFamily: config.fontFamily }}>
              Primary text preview
            </p>
            <p style={{ color: config.textSecondary, fontFamily: config.fontFamily }}>
              Secondary text preview
            </p>
            <p
              className="text-2xl font-bold mt-2"
              style={{ color: config.winText, fontFamily: config.fontFamilyNumbers }}
            >
              1,234,567
            </p>
          </div>
          <div className="flex gap-2">
            <div
              className="w-8 h-8 rounded"
              style={{ backgroundColor: config.primary }}
              title="Primary"
            />
            <div
              className="w-8 h-8 rounded"
              style={{ backgroundColor: config.secondary }}
              title="Secondary"
            />
            <div
              className="w-8 h-8 rounded"
              style={{ backgroundColor: config.accent }}
              title="Accent"
            />
            <div
              className="w-8 h-8 rounded border border-white/20"
              style={{ backgroundColor: config.winGlow, boxShadow: `0 0 10px ${config.winGlow}` }}
              title="Win Glow"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
